import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, Button, IconButton,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    CircularProgress, Divider
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useGetSettingByKeyQuery, useUpsertSettingMutation } from "../../../../reducers/settingsApi";
import UpdateButton from "./UpdateButton";
import { toast } from "react-toastify";

const ManageMapIps = () => {
    const settingKey = "app.setting.mapIp";
    const { data: settingRes, isLoading: isFetching } = useGetSettingByKeyQuery(settingKey);
    const [upsertSetting, { isLoading: isUpdating }] = useUpsertSettingMutation();

    const [ips, setIps] = useState([]);
    const [newIp, setNewIp] = useState("");

    useEffect(() => {
        if (settingRes?.data?.value) {
            setIps(Array.isArray(settingRes.data.value) ? settingRes.data.value : [settingRes.data.value]);
        }
    }, [settingRes]);

    const updateSettings = async (updatedIps) => {
        try {
            const payload = {
                key: settingKey,
                value: updatedIps,
                description: "List of map Ips",
                category: "app",
                isPublic: true
            };

            await upsertSetting(payload).unwrap();
            setIps(updatedIps);
            setNewIp("");
            toast.success("Map IPs updated successfully");
        } catch (error) {
            console.error("Failed to save IPs:", error);
            toast.error(error?.data?.message || "Failed to update Map IPs");
        }
    };

    const handleRemoveIp = async (ipToRemove) => {
        const updatedIps = ips.filter(ip => ip !== ipToRemove);
        await updateSettings(updatedIps);
    };

    const handleSave = async () => {
        let updatedIps = [...ips];
        const trimmed = newIp.trim();

        if (trimmed) {
            const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
            if (!ipRegex.test(trimmed)) {
                toast.error("Please enter a valid IP address");
                return;
            }

            if (updatedIps.includes(trimmed)) {
                toast.warning("IP already exists in the list");
            } else {
                updatedIps = [...updatedIps, trimmed];
            }
        }

        await updateSettings(updatedIps);
    };

    if (isFetching) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 ,color:"#000"}}>
                Manage Map IPs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 ,color:"#000"}}>
                Enter an IP address and click the update button below to add it to the list. You can also remove existing IPs and click update to save changes.
            </Typography>

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Add New IP Address"
                    placeholder="e.g. 192.168.1.1"
                    value={newIp}
                    onChange={(e) => setNewIp(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                />
            </Box>

            <Divider />

            <List sx={{ maxHeight: "300px", overflowY: "auto" }}>
                {ips.length === 0 ? (
                    <Typography sx={{ textAlign: "center", py: 4, color: "#999", fontStyle: "italic" }}>
                        No IPs added yet.
                    </Typography>
                ) : (
                    ips.map((ip, index) => (
                        <ListItem key={index} divider>
                            <ListItemText primary={ip} />
                            <ListItemSecondaryAction>
                                <IconButton edge="end" onClick={() => handleRemoveIp(ip)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))
                )}
            </List>

            <Box sx={{ mt: 2 }}>
                <UpdateButton
                    position="relative"
                    brand="Map IPs"
                    loading={isUpdating}
                    onClick={handleSave}
                />
            </Box>
        </Box>
    );
};

export default ManageMapIps;
